import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  TablePagination,
  Grid
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSnackbar } from 'notistack';
import taxService from '../../services/taxService';

const TaxCodes = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State variables
  const [taxCodes, setTaxCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredTaxCodes, setFilteredTaxCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingTaxCodes, setSyncingTaxCodes] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Fetch tax codes on component mount
  useEffect(() => {
    fetchTaxCodes();
    fetchCategories();
  }, []);
  
  // Filter tax codes when search query, category, or tax codes change
  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, taxCodes]);
  
  // Function to fetch tax codes from API
  const fetchTaxCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await taxService.getTaxCodes();
      logger.log('Tax codes loaded:', data);
      
      if (Array.isArray(data)) {
        setTaxCodes(data);
        setFilteredTaxCodes(data);
      } else {
        logger.error('Invalid tax codes data format:', data);
        setError('Failed to load tax codes - invalid data format');
      }
    } catch (err) {
      logger.error('Error fetching tax codes:', err);
      setError('Failed to load tax codes: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Function to fetch categories from API
  const fetchCategories = async () => {
    try {
      const data = await taxService.getTaxCodeCategories();
      logger.log('Categories loaded:', data);
      
      if (Array.isArray(data)) {
        setCategories(data);
      } else {
        logger.error('Invalid categories data format:', data);
      }
    } catch (err) {
      logger.error('Error fetching categories:', err);
      // Don't set error state here to avoid blocking the main functionality
    }
  };
  
  // Function to sync the tax code catalogue
  const handleSyncTaxCodes = async () => {
    try {
      setSyncingTaxCodes(true);
      setError(null);
      
      const result = await taxService.syncTaxCodes();
      logger.log('Sync result:', result);
      
      // Refresh the list after sync
      await fetchTaxCodes();
      await fetchCategories();
      
      enqueueSnackbar(result?.in_progress ? 'Refreshing the tax code catalogue in the background' : 'Tax codes synced successfully',
        { variant: result?.in_progress ? 'info' : 'success' });
    } catch (err) {
      logger.error('Error syncing tax codes:', err);
      setError('Failed to sync tax codes: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
      enqueueSnackbar('Failed to sync tax codes', { variant: 'error' });
    } finally {
      setSyncingTaxCodes(false);
    }
  };
  
  // Function to apply filters to tax codes
  const applyFilters = () => {
    let result = [...taxCodes];
    
    // Apply category filter
    if (selectedCategory) {
      result = result.filter(item => item.category_type === selectedCategory);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.id.toLowerCase().includes(query) || 
        item.name.toLowerCase().includes(query) || 
        (item.description && item.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredTaxCodes(result);
    setPage(0); // Reset to first page when filters change
  };
  
  // Handler for category filter changes
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };
  
  // Handler for search query changes
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  
  // Handler for pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handler for rows per page changes
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get color for category chip
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Physical goods':
        return 'primary';
      case 'Digital products':
        return 'secondary';
      case 'Services':
        return 'success';
      default:
        return 'default';
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Card>
      <CardHeader 
        title="Tax Codes" 
        subheader="Browse and filter product tax codes"
        action={
          <IconButton 
            onClick={handleSyncTaxCodes} 
            disabled={syncingTaxCodes}
            title="Refresh the tax code catalogue"
            color="primary"
          >
            {syncingTaxCodes ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph>
            Tax codes help determine the correct tax treatment for goods and services in different jurisdictions.
            Use these codes when configuring your products for tax calculation.
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Tax Codes"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by ID, name, or description"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="category-filter-label">Filter by Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Filter by Category"
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>All Categories</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Typography variant="body2" color="text.secondary" display="flex" alignItems="center">
            <InfoIcon fontSize="small" sx={{ mr: 1 }} />
            Showing {filteredTaxCodes.length} of {taxCodes.length} total tax codes
          </Typography>
        </Box>
        
        <TableContainer component={Paper} variant="outlined">
          <Table sx={{ minWidth: 650 }} aria-label="tax codes table">
            <TableHead>
              <TableRow>
                <TableCell>Tax Code ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTaxCodes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((taxCode) => (
                  <TableRow key={taxCode.id}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" fontFamily="monospace">
                        {taxCode.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{taxCode.name}</TableCell>
                    <TableCell>
                      {taxCode.category_type ? (
                        <Chip 
                          label={taxCode.category_type} 
                          size="small" 
                          color={getCategoryColor(taxCode.category_type)} 
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Uncategorized" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{taxCode.description}</TableCell>
                  </TableRow>
                ))}
              {filteredTaxCodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No tax codes found matching the current filters
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredTaxCodes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
};

export default TaxCodes; 